/**
 * EPUB content (iframe) reading styles: color themes, font family, line height.
 *
 * Applied via epub.js `themes.default` + `themes.override`.
 * Prefer overrides for color/background/font — `themes.select` is unreliable
 * when switching back (epub.js #1208).
 *
 * Annotation SVG marks live in the parent document (marks-pane); blend modes
 * are handled in EpubViewer.css via [data-theme], not here.
 */

const EPUB_CONTENT_THEMES = {
  light: {
    background: "#ffffff",
    color: "#1a1a1a",
    link: "#2563eb",
    epubOnly: false,
  },
  dark: {
    background: "#111111",
    color: "#e6e6e6",
    link: "#6ea8fe",
    epubOnly: false,
  },
  mint: {
    background: "#c8e6c9",
    color: "#1b3320",
    link: "#2e7d32",
    epubOnly: true,
  },
  sepia: {
    background: "#e8d5b7",
    color: "#5b4636",
    link: "#8b5a2b",
    epubOnly: true,
  },
};

/** Play Books–style typefaces (system stacks — no webfont license needed). */
export const EPUB_FONT_OPTIONS = [
  { id: "original", family: null },
  {
    id: "sans",
    family:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  {
    id: "serif",
    family: 'Georgia, "Times New Roman", Times, serif',
  },
];

/** Line-height steps (unitless), similar to Play Books spacing control. */
export const EPUB_LINE_HEIGHT_MIN = 1.2;
export const EPUB_LINE_HEIGHT_MAX = 2.4;
export const EPUB_LINE_HEIGHT_STEP = 0.15;
export const EPUB_LINE_HEIGHT_DEFAULT = 1.5;

/** DisplayMenu swatches — colors stay in sync with theme backgrounds. */
export const READER_THEME_SWATCHES = Object.entries(EPUB_CONTENT_THEMES).map(
  ([id, theme]) => ({
    id,
    color: theme.background,
    epubOnly: theme.epubOnly,
  }),
);

export const isEpubOnlyTheme = (themeId) =>
  Boolean(EPUB_CONTENT_THEMES[themeId]?.epubOnly);

export const isValidReaderTheme = (themeId) =>
  Boolean(EPUB_CONTENT_THEMES[themeId]);

export const normalizeReaderTheme = (themeId) =>
  isValidReaderTheme(themeId) ? themeId : "dark";

export const isValidEpubFont = (fontId) =>
  EPUB_FONT_OPTIONS.some((opt) => opt.id === fontId);

const normalizeEpubFont = (fontId) =>
  isValidEpubFont(fontId) ? fontId : "original";

export const getEpubFontFamily = (fontId) => {
  const opt = EPUB_FONT_OPTIONS.find(
    (item) => item.id === normalizeEpubFont(fontId),
  );
  return opt?.family ?? null;
};

export const normalizeEpubLineHeight = (value) => {
  // Number(null) === 0 and Number("") === 0 — those must not clamp to MIN (1.2).
  if (value == null || value === "") return EPUB_LINE_HEIGHT_DEFAULT;
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return EPUB_LINE_HEIGHT_DEFAULT;
  const stepped =
    Math.round(num / EPUB_LINE_HEIGHT_STEP) * EPUB_LINE_HEIGHT_STEP;
  const clamped = Math.min(
    EPUB_LINE_HEIGHT_MAX,
    Math.max(EPUB_LINE_HEIGHT_MIN, stepped),
  );
  return Math.round(clamped * 100) / 100;
};

export const stepEpubLineHeight = (current, direction) => {
  const base = normalizeEpubLineHeight(current);
  const delta = direction < 0 ? -EPUB_LINE_HEIGHT_STEP : EPUB_LINE_HEIGHT_STEP;
  return normalizeEpubLineHeight(base + delta);
};

const getEpubContentTheme = (themeId) =>
  EPUB_CONTENT_THEMES[normalizeReaderTheme(themeId)] ||
  EPUB_CONTENT_THEMES.light;

/**
 * epub.js addStylesheetRules() appends without clearing, so repeated
 * themes.default() calls stack stale font-family / line-height rules.
 * Wipe the injected "default" stylesheet before re-applying.
 */
const resetDefaultStylesheet = (rendition) => {
  try {
    const contentsList = rendition.getContents?.() || [];
    contentsList.forEach((contents) => {
      const doc = contents?.document;
      if (!doc) return;
      const styleEl = doc.getElementById("epubjs-inserted-css-default");
      if (!styleEl) return;
      styleEl.textContent = "";
      try {
        const sheet = styleEl.sheet;
        if (!sheet) return;
        while (sheet.cssRules.length > 0) {
          sheet.deleteRule(0);
        }
      } catch {
        // Cross-origin / empty sheet — textContent clear is enough
      }
    });
  } catch (err) {
    console.warn("Failed to reset EPUB default stylesheet:", err);
  }
};

const clearThemeOverride = (rendition, property) => {
  try {
    if (typeof rendition?.themes?.removeOverride === "function") {
      rendition.themes.removeOverride(property);
      return;
    }
    if (rendition?.themes?._overrides) {
      delete rendition.themes._overrides[property];
    }
    const contentsList = rendition.getContents?.() || [];
    contentsList.forEach((contents) => {
      // contents.css(prop) with no value removes the inline override
      if (typeof contents?.css === "function") {
        contents.css(property);
        return;
      }
      contents?.document?.body?.style?.removeProperty(property);
    });
  } catch (err) {
    console.warn(`Failed to clear EPUB theme override (${property}):`, err);
  }
};

const buildEpubContentThemeRules = (options = {}) => {
  const {
    themeId = "light",
    fontSizePercent = null,
    fontFamily = null,
    lineHeight = null,
  } = options;
  const theme = getEpubContentTheme(themeId);

  const body = {
    "background-color": theme.background,
    color: theme.color,
  };
  if (fontSizePercent != null && Number.isFinite(fontSizePercent)) {
    body["font-size"] = `${fontSizePercent}%`;
  }
  if (fontFamily) {
    body["font-family"] = fontFamily;
  }
  if (lineHeight != null && Number.isFinite(lineHeight)) {
    body["line-height"] = String(lineHeight);
  }

  return {
    html: {
      "background-color": theme.background,
    },
    body,
    a: {
      color: theme.link,
    },
  };
};

const applyEpubContentThemeOverrides = (
  rendition,
  { themeId, fontFamily, lineHeight },
) => {
  if (!rendition?.themes?.override) return;
  const theme = getEpubContentTheme(themeId);
  try {
    rendition.themes.override("color", theme.color, true);
    rendition.themes.override("background-color", theme.background, true);

    if (fontFamily) {
      rendition.themes.override("font-family", fontFamily, true);
    } else {
      clearThemeOverride(rendition, "font-family");
    }

    if (lineHeight != null && Number.isFinite(lineHeight)) {
      rendition.themes.override("line-height", String(lineHeight), true);
    } else {
      clearThemeOverride(rendition, "line-height");
    }
  } catch (err) {
    console.warn("Failed to apply EPUB theme overrides:", err);
  }
};

/**
 * Apply content theme + typography to a rendition.
 * Local-only DOM/CSS — no network.
 */
export const applyEpubReaderTheme = (rendition, options = {}) => {
  if (!rendition?.themes?.default) return;

  const {
    themeId = "light",
    scale = 1,
    isFixedLayout = false,
    fontId = "original",
    lineHeight = EPUB_LINE_HEIGHT_DEFAULT,
    extraRules = {},
  } = options;

  const fontSizePercent =
    !isFixedLayout && typeof scale === "number" ? scale * 100 : null;
  const fontFamily = isFixedLayout ? null : getEpubFontFamily(fontId);
  const resolvedLineHeight = isFixedLayout
    ? null
    : normalizeEpubLineHeight(lineHeight);

  try {
    resetDefaultStylesheet(rendition);
    rendition.themes.default({
      ...extraRules,
      ...buildEpubContentThemeRules({
        themeId,
        fontSizePercent,
        fontFamily,
        lineHeight: resolvedLineHeight,
      }),
    });
    applyEpubContentThemeOverrides(rendition, {
      themeId,
      fontFamily,
      lineHeight: resolvedLineHeight,
    });
  } catch (err) {
    console.warn("Failed to apply EPUB reader theme:", err);
  }
};
